import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { breakpoints, Column, Columns } from '@etg/wings';
import {
  BoundNew,
  BundleProduct,
  CabinBaggageTripSummary,
  ChargingCurrency,
  CheckedBaggageSummary,
  IncludedCheckedBaggage,
  PersonalItemTripSummary,
  TravelerPrice,
  TripCharacteristics,
} from '@eti/types';
import { css, cx } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import { DangerousHtmlContent, Notification, variants } from '@eti/components';
import {
  allInclusiveProtection,
  CHECK_IN_BAGGAGE,
  seatMap,
  tripCancellationProtection,
} from '@eti/products';
import { connect } from 'react-redux';
import { change, Form, formValueSelector } from 'redux-form';
import { useScrollTo } from '@eti/utils';
import { InView } from 'react-intersection-observer';
import { useProductCartNames } from '../../../common/custom-hooks/productCartNames';
import RebookingBanner from '../../../common/components/RebookingBanner';
import useSelfServiceRebooking from '../../../common/self-service-rebooking/useSelfServiceRebooking';
import { VALID_WITH_VOUCHER } from '../../../constants/tripCharacteristics';
import { MULTI_STOP, ONE_WAY, RETURN } from '../../../constants/tripTypesConstants';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../utils/carriers';
import { useCheapestPaymentMethod } from '../../../utils/paymentMethods';
import getGraphQlSelectionForAllProducts from '../../../utils/getGraphQlSelectionForAllProducts';
import TravelerDetailsForm from '../components/TravelerDetailsForm';
import TravelerDetailsCart from '../components/TravelerDetailsCart';
import {
  AGENCY_FORM_SECTION,
  agencyFields,
  CONTACT_INFO_FORM_SECTION,
  contactFields,
  contactTypes,
} from '../constants/formFields';
import { transformAgentInformationFormData } from '../utils/agentInformation';
import { getSeatMapExtraProductStructure } from '../utils/availableExtraProducts';
import { transformCartData } from '../utils/cartUtils';
import { transformContactInformationFormData } from '../utils/contactInformation';
import { generateSeatMapProductComponent } from '../utils/Format';
import { flattenProducts } from '../utils/products';
import { transformAncillaryBundles, transformTravelers } from '../utils/selection';
import BottomContent from '../components/TravelerDetailsBottomContent';
import { SEATMAP_PRODUCT_QUERY } from './queries';

const { CONTACT_TYPE, EMAIL, EMERGENCY_PHONE, PHONE } = contactFields;
const { AGENT_EMAIL, AGENT_PHONE } = agencyFields;

const formStyles = css`
  display: flex;
  flex-direction: column;
`;

const bottomContentStyles = css`
  padding-bottom: 32px;

  @media (min-width: ${breakpoints._970}) {
    padding-bottom: 0;
  }
`;

const TravelerDetailsFormContainer = ({
  addedProductsNumber,
  addToCart,
  agentEmailAddress,
  agentPhoneNumber,
  ancillaryBundleSelectedValue,
  asyncValidate,
  availableExtraProducts,
  bottomContent = { isAnyTouched: false, history: {}, isValid: false },
  bounds,
  bundleData,
  bundleDataTDPage,
  cabinBaggageTripSummary,
  chargingCurrency,
  checkedBaggageSummary,
  countries,
  domesticCountryCode,
  emailAddress,
  emergencyPhoneNumber,
  form,
  headerAndInfoHeight,
  includedCheckedBaggage,
  includedProducts,
  isAncillaryBundleEmptyOptionSelected,
  isAncillaryBundlingEnabled,
  isCabinBaggageAvailableOrIncluded,
  isCheckedBaggageAvailableOrIncluded,
  isLoading,
  isMultiStop,
  isOnSectionOne,
  isSeatMapModalOpen,
  isSeatMapSelected,
  isSubmitting,
  isTravelArrangerSelected,
  onDateOfBirthBlur,
  onSubmit,
  paymentMethodPrices,
  personalItemTripSummary,
  phoneNumber,
  products,
  seatMapCallback,
  selectionKey,
  setIsOnSectionOne,
  setIsSeatMapModalOpen,
  setShowInfoReviewModal,
  setShowReviewModal,
  shareableUrl,
  shouldShowEmergencyContactInformation,
  shouldShowProductsPerTraveler,
  shouldShowTravelDocumentType,
  showSeatMapModal,
  travelerFormProducts,
  travelerNames,
  travelerPrices,
  travelers,
  triggerEmailAddressAsyncValidation,
  triggerPhoneAsyncValidation,
  triggerAgentEmailAddressAsyncValidation,
  triggerAgentPhoneAsyncValidation,
  tripId,
  tripCharacteristics,
  tripOperatingCarriers,
  tripProviderSystems,
  type,
  updateAvailableProducts,
  voucherAmount,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const availableExtraProductsRef = useRef(availableExtraProducts);
  const [showVoucherNotValidMessage, setShowVoucherNotValidMessage] = useState(true);
  const [travelersData, setTravelersData] = useState(travelers);
  const [travelerPricesData, setTravelerPricesData] = useState(travelerPrices);
  const bottomContentRef = useRef(null);
  const { isSelfServiceRebooking, selfServiceRebookingTravelers } = useSelfServiceRebooking();
  const productNames = useProductCartNames();
  const selectedPaymentMethod = useCheapestPaymentMethod(paymentMethodPrices);
  const cartData = transformCartData(
    travelersData,
    travelerPricesData,
    travelerNames,
    voucherAmount,
  );
  const checkInBaggageProduct = travelerFormProducts?.find(
    (product) => product.id === CHECK_IN_BAGGAGE.id,
  );
  const { scrollVertical } = useScrollTo();
  const { isValid } = bottomContent;
  const [wizardSteps, setWizardSteps] = useState([1, 1]);
  const submitAndMove = async (
    contactInformation,
    extraProductSelection,
    ancillaryBundleSelection,
    travelerSelection,
    subscribeToNewsletter,
    travelSeller,
    emergencyContactInformation,
  ) => {
    await addToCart({
      variables: {
        selectionKey,
        contactInformation,
        extraProductSelection,
        ancillaryBundleSelection,
        travelers: travelerSelection,
        travelSeller,
        subscribeToNewsletter,
        emergencyContactInformation,
      },
    });
  };
  const [getSeatMap, { data: seatMapData, called: isSeatMapQueryCalled }] = useLazyQuery(
    SEATMAP_PRODUCT_QUERY,
    {
      variables: { productId: seatMap.id },
      fetchPolicy: 'no-cache',
    },
  );

  const isDynamicBookingFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled');

  useEffect(() => {
    if (!isSeatMapQueryCalled) {
      getSeatMap();
    }

    if (isDynamicBookingFlowEnabled) {
      return;
    }

    const isSeatMapExtraProductAvailable = flattenProducts(products).some(
      (product) => Number(product.id) === seatMap.id,
    );

    if (isSeatMapQueryCalled && seatMapData && !isSeatMapExtraProductAvailable) {
      const seatMapExtraProductStructure = getSeatMapExtraProductStructure(
        seatMapData.ancillaryItem,
      );
      if (!seatMapExtraProductStructure) {
        return;
      }

      const seatMapProductComponent = generateSeatMapProductComponent(
        travelersData,
        bounds,
      )({ ...seatMapExtraProductStructure });

      updateAvailableProducts((prevProducts) => [
        [...prevProducts?.[0], seatMapProductComponent],
        [...(prevProducts?.[1] ?? [])],
      ]);

      availableExtraProductsRef.current = [
        ...availableExtraProductsRef.current,
        seatMapProductComponent,
      ];
    }
  }, [
    bounds,
    getSeatMap,
    isSeatMapQueryCalled,
    seatMapData,
    updateAvailableProducts,
    products,
    isDynamicBookingFlowEnabled,
    travelersData,
  ]);

  const dateFormat = p('IbeClient.TravelerInput.dateInputFormat');

  const preloadPaymentPage = async (inView) => {
    if (inView && !isDynamicBookingFlowEnabled) {
      const { PaymentPage } = await import('../../payment');
      PaymentPage.preload();
    }
  };

  const handleSubmit = async (values) => {
    const travelSeller = transformAgentInformationFormData(values, countries);
    const { contactInformation, subscribeToNewsletter, emergencyContactInformation } =
      transformContactInformationFormData(values, countries);
    const travelerSelection = transformTravelers(values, travelersData, dateFormat);

    const ancillaryBundleSelection = transformAncillaryBundles(
      values,
      isAncillaryBundlingEnabled,
      bundleDataTDPage,
    );
    const productSelection = getGraphQlSelectionForAllProducts({
      form,
      values,
    });

    await submitAndMove(
      contactInformation,
      productSelection,
      ancillaryBundleSelection,
      travelerSelection,
      subscribeToNewsletter,
      travelSeller,
      shouldShowEmergencyContactInformation ? emergencyContactInformation : undefined,
    );
  };

  const shouldShowSeatMapModal =
    flattenProducts(products).some((product) => Number(product.id) === seatMap.id) &&
    p('IbeClient.TravelerDetails.Modal') === 'SEATMAP';
  const voucherNotValid = !tripCharacteristics.includes(VALID_WITH_VOUCHER);
  const handleCloseVoucherNotValid = () => {
    setShowVoucherNotValidMessage(false);
    sessionStorage.setItem('hasClosedNotValidVoucherMessage', true);
  };
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    p('BookingFlow.SupplierCostMarkupBreakdown.Carriers'),
    p('BookingFlow.HideTaxIfZero.Carriers'),
  );

  const updateTravelerPricesOnEditPassenger = (newTravelersPrice, newTravelers) => {
    setTravelersData(newTravelers);
    setTravelerPricesData(newTravelersPrice);
  };
  const [hasSecondaryProducts, setHasSecondaryProducts] = useState(products[1]?.length > 0);

  const handleRevealNextSection = () => {
    setWizardSteps([0, 0, 1]);
    setIsOnSectionOne(false);
    scrollVertical('smooth', 0);
  };

  return (
    <>
      <Columns collapseBelow={breakpoints._970}>
        <Column width={{ [breakpoints._0]: '4/4', [breakpoints._970]: '4/6' }}>
          {isSelfServiceRebooking && !voucherNotValid && <RebookingBanner />}
          {isSelfServiceRebooking && voucherNotValid && showVoucherNotValidMessage && (
            <Notification
              dataTestId="travelerDetailsForm-voucherNotValid"
              onClose={handleCloseVoucherNotValid}
              title={t('SelfServiceRebooking.VoucherNotValid.Label')}
              variant={variants.WARNING}
            >
              <DangerousHtmlContent content={t('SelfServiceRebooking.VoucherNotValid.Text')} />
            </Notification>
          )}
          <Form
            className={formStyles}
            data-testid="travelerDetailsForm"
            onSubmit={onSubmit((values) => handleSubmit(values))}
          >
            <TravelerDetailsForm
              agentEmailAddress={agentEmailAddress}
              agentPhoneNumber={agentPhoneNumber}
              ancillaryBundleSelectedValue={ancillaryBundleSelectedValue}
              asyncValidate={asyncValidate}
              bottomContent={bottomContent}
              bottomContentRef={bottomContentRef}
              bounds={bounds}
              bundleData={bundleData}
              cabinBaggageTripSummary={cabinBaggageTripSummary}
              checkedBaggageSummary={checkedBaggageSummary}
              countries={countries}
              dateFormat={dateFormat}
              domesticCountryCode={domesticCountryCode}
              emailAddress={emailAddress}
              emergencyPhoneNumber={emergencyPhoneNumber}
              form={form}
              hasSecondaryProducts={hasSecondaryProducts}
              includedCheckedBaggage={includedCheckedBaggage}
              includedProducts={includedProducts}
              isAncillaryBundleEmptyOptionSelected={isAncillaryBundleEmptyOptionSelected}
              isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
              isCabinBaggageAvailableOrIncluded={isCabinBaggageAvailableOrIncluded}
              isCheckedBaggageAvailableOrIncluded={isCheckedBaggageAvailableOrIncluded}
              isLoading={isLoading || isSubmitting}
              isMultiStop={isMultiStop}
              isOnSectionOne={isOnSectionOne}
              isSeatMapModalOpen={isSeatMapModalOpen}
              isSeatMapSelected={isSeatMapSelected}
              isSelfServiceRebooking={isSelfServiceRebooking && !voucherNotValid}
              isTravelArrangerSelected={isTravelArrangerSelected}
              onDateOfBirthBlur={onDateOfBirthBlur}
              personalItemTripSummary={personalItemTripSummary}
              phoneNumber={phoneNumber}
              preloadPaymentPage={preloadPaymentPage}
              products={products}
              seatMapCallback={seatMapCallback}
              selectionKey={selectionKey}
              selfServiceRebookingTravelers={selfServiceRebookingTravelers}
              setHasSecondaryProducts={setHasSecondaryProducts}
              setIsOnSectionOne={setIsOnSectionOne}
              setIsSeatMapModalOpen={setIsSeatMapModalOpen}
              setShowInfoReviewModal={setShowInfoReviewModal}
              setShowReviewModal={setShowReviewModal}
              setWizardSteps={setWizardSteps}
              shareableUrl={shareableUrl}
              shouldShowEmergencyContactInformation={shouldShowEmergencyContactInformation}
              shouldShowProductsPerTraveler={shouldShowProductsPerTraveler}
              shouldShowSeatMapModal={shouldShowSeatMapModal}
              shouldShowTravelDocumentType={shouldShowTravelDocumentType}
              showSeatMapModal={showSeatMapModal}
              travelerFormProducts={travelerFormProducts}
              travelerPrices={travelerPricesData}
              travelers={travelersData}
              triggerAgentEmailAddressAsyncValidation={triggerAgentEmailAddressAsyncValidation}
              triggerAgentPhoneAsyncValidation={triggerAgentPhoneAsyncValidation}
              triggerEmailAddressAsyncValidation={triggerEmailAddressAsyncValidation}
              triggerPhoneAsyncValidation={triggerPhoneAsyncValidation}
              tripCharacteristics={tripCharacteristics}
              tripId={tripId}
              tripOperatingCarriers={tripOperatingCarriers}
              type={type}
              updateTravelerPricesOnEditPassenger={updateTravelerPricesOnEditPassenger}
              wizardSteps={wizardSteps}
            />
          </Form>
        </Column>
        <TravelerDetailsCart
          {...cartData}
          addedProductsNumber={addedProductsNumber}
          bottomContentRef={bottomContentRef}
          bounds={bounds}
          bundleData={bundleData}
          bundleDataTDPage={bundleDataTDPage}
          cabinBaggageTripSummary={cabinBaggageTripSummary}
          chargingCurrency={chargingCurrency}
          checkedBaggageSummary={checkedBaggageSummary}
          countries={countries}
          headerAndInfoHeight={headerAndInfoHeight}
          isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
          isMultiStop={isMultiStop}
          isOnSectionOne={isOnSectionOne}
          marketingCarrierNames={marketingCarrierNames}
          numberOfTravelers={travelersData?.length}
          paymentMethodPrices={paymentMethodPrices}
          personalItemTripSummary={personalItemTripSummary}
          productNames={productNames}
          selectedPaymentMethod={selectedPaymentMethod}
          shareableUrl={shareableUrl}
          travelerFormProducts={travelerFormProducts}
          tripCharacteristics={tripCharacteristics}
          tripProviderSystems={tripProviderSystems}
          type={type}
        />
      </Columns>
      <Columns>
        <Column width={{ [breakpoints._0]: '4/4', [breakpoints._970]: '4/6' }}>
          <InView
            as="div"
            data-testid="travelerDetails-bottomContent-intersectionWrapper"
            onChange={preloadPaymentPage}
            triggerOnce
          >
            <div
              className={cx({
                [bottomContentStyles]:
                  p('IbeClient.MenuAndFooter.Hide') || p('CustomerWeb.HideFooter.Enabled'),
              })}
              ref={bottomContentRef}
            >
              <BottomContent
                asyncValidate={asyncValidate}
                checkInBaggageProduct={checkInBaggageProduct}
                emailAddress={emailAddress}
                form={form}
                handleRevealNextSection={handleRevealNextSection}
                hasSecondaryProducts={hasSecondaryProducts}
                isFormValid={isValid}
                isLoading={isLoading}
                phoneNumber={phoneNumber}
                setShowInfoReviewModal={setShowInfoReviewModal}
                setShowReviewModal={setShowReviewModal}
                shouldShowSeatMapModal={shouldShowSeatMapModal && !isSeatMapSelected}
                showSeatMapModal={showSeatMapModal}
                wizardSteps={wizardSteps}
              />
            </div>
          </InView>
        </Column>
      </Columns>
    </>
  );
};

TravelerDetailsFormContainer.propTypes = {
  addedProductsNumber: PropTypes.number,
  addToCart: PropTypes.func,
  agencyInformation: PropTypes.shape({
    agentFirstName: PropTypes.string,
    agentLastName: PropTypes.string,
    name: PropTypes.string,
    officeId: PropTypes.string,
  }),
  agentEmailAddress: PropTypes.string,
  agentPhoneNumber: PropTypes.string,
  ancillaryBundleSelectedValue: PropTypes.string,
  asyncValidate: PropTypes.func,
  availableExtraProducts: PropTypes.array,
  bottomContent: PropTypes.shape({
    history: PropTypes.objectOf(PropTypes.any),
    isAnyTouched: PropTypes.bool,
    isValid: PropTypes.bool,
  }),
  bounds: PropTypes.arrayOf(BoundNew),
  bundleData: BundleProduct,
  bundleDataTDPage: BundleProduct,
  cabinBaggageTripSummary: CabinBaggageTripSummary,
  chargingCurrency: ChargingCurrency,
  checkedBaggageSummary: CheckedBaggageSummary,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      phoneCountryId: PropTypes.string,
      phoneCountryPrefix: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  domesticCountryCode: PropTypes.string,
  emailAddress: PropTypes.string,
  emergencyPhoneNumber: PropTypes.string,
  form: PropTypes.string,
  headerAndInfoHeight: PropTypes.number,
  includedCheckedBaggage: IncludedCheckedBaggage,
  includedProducts: PropTypes.arrayOf(PropTypes.any),
  isAncillaryBundleEmptyOptionSelected: PropTypes.bool,
  isAncillaryBundlingEnabled: PropTypes.bool,
  isAnyTouched: PropTypes.bool,
  isCabinBaggageAvailableOrIncluded: PropTypes.bool,
  isCheckedBaggageAvailableOrIncluded: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  isOnSectionOne: PropTypes.bool,
  isSeatMapModalOpen: PropTypes.bool,
  isSeatMapSelected: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isTravelArrangerSelected: PropTypes.bool,
  onDateOfBirthBlur: PropTypes.func,
  onSubmit: PropTypes.func,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  personalItemTripSummary: PersonalItemTripSummary,
  phoneNumber: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.any),
  seatMapCallback: PropTypes.func,
  selectionKey: PropTypes.string,
  setIsOnSectionOne: PropTypes.func,
  setIsSeatMapModalOpen: PropTypes.func,
  setShowInfoReviewModal: PropTypes.func,
  setShowReviewModal: PropTypes.func,
  shareableUrl: PropTypes.string,
  shouldShowEmergencyContactInformation: PropTypes.bool,
  shouldShowProductsPerTraveler: PropTypes.bool,
  shouldShowTravelDocumentType: PropTypes.bool,
  showSeatMapModal: PropTypes.func,
  travelerFormProducts: PropTypes.arrayOf(PropTypes.any),
  travelerNames: PropTypes.arrayOf(
    PropTypes.shape({
      fullName: PropTypes.string,
      initials: PropTypes.string,
      travelerId: PropTypes.string,
    }),
  ),
  travelerPrices: PropTypes.arrayOf(TravelerPrice),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      age: PropTypes.number,
      ageType: PropTypes.string,
      boundIndex: PropTypes.number,
      countries: PropTypes.arrayOf(PropTypes.any),
      marketingMajorityCarrier: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.object),
      shouldShowFrequentFlyerCard: PropTypes.bool,
      travelDocument: PropTypes.shape({
        requireTravelDocumentExpiryDate: PropTypes.oneOf(['OPTIONAL', 'REQUIRED', 'NOT_REQUIRED']),
        shouldShowTravelDocumentCpfNumber: PropTypes.bool,
        shouldShowTravelDocumentDateOfBirth: PropTypes.bool,
        shouldShowTravelDocumentIssueCity: PropTypes.bool,
        shouldShowTravelDocumentIssueDate: PropTypes.bool,
        shouldShowTravelDocumentNationality: PropTypes.bool,
        shouldShowTravelDocumentPassportNumber: PropTypes.bool,
      }),
      travelTime: PropTypes.string,
    }),
  ),
  triggerAgentEmailAddressAsyncValidation: PropTypes.func,
  triggerAgentPhoneAsyncValidation: PropTypes.func,
  triggerEmailAddressAsyncValidation: PropTypes.func,
  triggerPhoneAsyncValidation: PropTypes.func,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripId: PropTypes.string,
  tripOperatingCarriers: PropTypes.arrayOf(PropTypes.string),
  tripProviderSystems: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf([MULTI_STOP, ONE_WAY, RETURN]),
  updateAvailableProducts: PropTypes.func,
  voucherAmount: PropTypes.shape({
    value: PropTypes.number,
  }),
};

const mapStateToProps = (state, { form }) => {
  const agentEmailAddress = formValueSelector(form)(state, `${AGENCY_FORM_SECTION}.${AGENT_EMAIL}`);
  const agentPhoneNumber = formValueSelector(form)(state, `${AGENCY_FORM_SECTION}.${AGENT_PHONE}`);
  const ancillaryBundleSelectedValue = formValueSelector(form)(state, 'ancillaryBundle');
  const emailAddress = formValueSelector(form)(state, `${CONTACT_INFO_FORM_SECTION}.${EMAIL}`);
  const emergencyPhoneNumber = formValueSelector(form)(
    state,
    `${CONTACT_INFO_FORM_SECTION}.${EMERGENCY_PHONE}`,
  );
  const isAncillaryBundleEmptyOptionSelected =
    ancillaryBundleSelectedValue && ancillaryBundleSelectedValue.value.value === 'BASIC';
  const isSeatMapSelected = Boolean(formValueSelector(form)(state, 'seatMap.value.value'));
  const isTravelArrangerSelected =
    formValueSelector(form)(state, `${CONTACT_INFO_FORM_SECTION}.${CONTACT_TYPE}`) ===
    contactTypes.ARRANGER;
  const phoneNumber = formValueSelector(form)(state, `${CONTACT_INFO_FORM_SECTION}.${PHONE}`);

  return {
    agentEmailAddress,
    agentPhoneNumber,
    ancillaryBundleSelectedValue: ancillaryBundleSelectedValue?.value.value,
    emailAddress,
    emergencyPhoneNumber,
    isAncillaryBundleEmptyOptionSelected,
    isSeatMapSelected,
    isTravelArrangerSelected,
    phoneNumber,
  };
};

const mapDispatchToProps = (
  dispatch,
  { form, asyncValidate, setIsSeatMapModalOpen, setSeatMapCallback },
) => ({
  showSeatMapModal: (callback = () => {}) => {
    dispatch(change(form, 'seatMap.value.value', 'true'));
    setIsSeatMapModalOpen(true);
    setSeatMapCallback(() => () => {
      callback();
    });
  },
  onDateOfBirthBlur:
    (id) =>
    ({ target }) => {
      const traveler = `travelers.traveler-${id}`;
      const { value } = target;
      dispatch(change(form, `${allInclusiveProtection.name}.${traveler}.dateOfBirth`, value, true));
      dispatch(
        change(form, `${allInclusiveProtection.name}.${traveler}.isRefetchingPrice`, true, true),
      );
      dispatch(
        change(form, `${tripCancellationProtection.name}.${traveler}.dateOfBirth`, value, true),
      );
      dispatch(
        change(
          form,
          `${tripCancellationProtection.name}.${traveler}.isRefetchingPrice`,
          true,
          true,
        ),
      );
    },
  triggerPhoneAsyncValidation(phoneNumber, phoneType) {
    return asyncValidate(`${CONTACT_INFO_FORM_SECTION}.${phoneType}`, phoneNumber, 'blur');
  },
  triggerEmailAddressAsyncValidation(emailAddress) {
    return asyncValidate(`${CONTACT_INFO_FORM_SECTION}.${EMAIL}`, emailAddress, 'blur');
  },
  triggerAgentPhoneAsyncValidation(phoneNumber, phoneType) {
    return asyncValidate(`${AGENCY_FORM_SECTION}.${phoneType}`, phoneNumber, 'blur');
  },
  triggerAgentEmailAddressAsyncValidation(emailAddress) {
    return asyncValidate(`${AGENCY_FORM_SECTION}.${AGENT_EMAIL}`, emailAddress, 'blur');
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TravelerDetailsFormContainer);
